import { computed, ref } from 'vue';

import { api, ApiError, type PaymentMethodsResponse } from '../net/api';
import { firstSupportedCurrency, methodSupportsCurrency } from '../core/payments/methods';
import type { DepositMethod } from '../core/payments/types';

/**
 * Payment-method choice for diamond purchases (t-game-core payments).
 *
 * Shared across the shop so every buy button charges through the same method.
 * The chosen method is remembered per browser (a convenience, never trusted by
 * the server — it re-checks method, currency and billing on every deposit).
 */
const STORAGE_KEY = 'sow:payment-method';

const methods = ref<DepositMethod[]>([]);
const loaded = ref(false);
const loadError = ref<string | null>(null);
const selectedId = ref<string | null>(readStored());

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function store(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* private mode — the choice just is not remembered */
  }
}

export interface CurrencySwitch {
  method: DepositMethod;
  from: string;
  to: string;
}

export function usePayments() {
  const selected = computed<DepositMethod | null>(
    () => methods.value.find((m) => m.id === selectedId.value) ?? methods.value[0] ?? null,
  );

  async function load(): Promise<void> {
    try {
      const res: PaymentMethodsResponse = await api.paymentMethods();
      methods.value = res.deposit.filter((m) => m.enabled);
      loadError.value = null;
    } catch (err) {
      loadError.value = err instanceof ApiError ? err.message : 'Payment methods are unavailable.';
    } finally {
      loaded.value = true;
    }
  }

  /**
   * Picks a method. When it cannot charge in the player's current currency
   * (BLIK → PLN only, Apple Pay / Open Banking → EUR only) the switch that is
   * needed is returned instead, for the caller to confirm first.
   */
  function choose(id: string, currentCurrency: string): CurrencySwitch | null {
    const method = methods.value.find((m) => m.id === id);
    if (!method) return null;
    if (!methodSupportsCurrency(method, currentCurrency)) {
      const to = firstSupportedCurrency(method, currentCurrency);
      if (to) return { method, from: currentCurrency, to };
    }
    selectedId.value = id;
    store(id);
    return null;
  }

  /** Confirmed currency switch: the player's currency preference changes, then the method. */
  async function confirmSwitch(sw: CurrencySwitch): Promise<void> {
    await api.updatePreferences({ currency: sw.to });
    selectedId.value = sw.method.id;
    store(sw.method.id);
  }

  return { methods, loaded, loadError, selected, load, choose, confirmSwitch };
}
